<template>
  <div class="stock-invoice-lines">
    <StockProductLineFinder
      v-if="!disabled"
      :disabled="disabled"
      class="mb-4"
      @add="addProductFromFinder"
    />

    <div v-if="!localLines.length" class="stock-invoice-lines__empty grey--text">
      {{ $t("STOCK.INVOICES.NO_LINES") }}
    </div>

    <article
      v-for="(line, index) in localLines"
      :key="lineKey(line, index)"
      class="stock-invoice-line"
    >
      <div class="stock-invoice-line__name">
        <span class="stock-invoice-line__index">{{ index + 1 }}</span>
        <div class="stock-invoice-line__description">
          <v-textarea
            v-if="!disabled"
            v-model="line.description"
            :placeholder="$t('STOCK.INVOICES.LINE_DESCRIPTION')"
            auto-grow
            rows="1"
            dense
            solo
            flat
            hide-details
            class="stock-invoice-line__textarea"
            @input="emitChange"
          />
          <div v-else class="stock-invoice-line__description-text">
            {{ line.description || "—" }}
          </div>
          <div v-if="lineProductMeta(line)" class="stock-invoice-line__meta text-caption grey--text">
            {{ lineProductMeta(line) }}
          </div>
        </div>
        <v-btn
          v-if="!disabled"
          icon
          small
          color="error"
          class="stock-invoice-line__remove"
          @click="removeLine(index)"
        >
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </div>

      <div class="stock-invoice-line__fields">
        <div class="stock-invoice-line__field">
          <div class="stock-invoice-line__label">{{ $t("STOCK.INVOICES.HS_CODE") }}</div>
          <StockLineField
            v-if="!disabled"
            v-model="line.hs_code"
            :disabled="disabled"
            @input="emitChange"
          />
          <div v-else class="stock-invoice-line__value">{{ line.hs_code || "—" }}</div>
        </div>
        <div class="stock-invoice-line__field">
          <div class="stock-invoice-line__label">{{ $t("STOCK.INVOICES.MEASURE") }}</div>
          <v-select
            v-if="!disabled"
            v-model="line.unit_id"
            :items="unitOptionsFor(line)"
            item-text="label"
            item-value="id"
            dense
            solo
            hide-details
            :disabled="unitsLoading"
            class="stock-invoice-unit"
            :menu-props="{ offsetY: true }"
            @change="onUnitSelected(line)"
          />
          <div v-else class="stock-invoice-line__value">{{ printedUnit(line) }}</div>
        </div>
        <div class="stock-invoice-line__field">
          <div class="stock-invoice-line__label">{{ $t("STOCK.INVOICES.QTY") }}</div>
          <StockLineField
            v-if="!disabled"
            v-model.number="line.quantity"
            type="number"
            :disabled="disabled"
            @focus="focusSelectAllInput"
            @input="onQtyOrPriceInput(line)"
          />
          <div v-else class="stock-invoice-line__value">{{ formatQty(line.quantity) }}</div>
        </div>
        <div class="stock-invoice-line__field">
          <div class="stock-invoice-line__label">{{ $t("STOCK.INVOICES.UNIT_PRICE_NET") }}</div>
          <StockLineField
            v-if="!disabled"
            v-model.number="line.unit_price_net"
            type="number"
            :hint="currencyCode"
            :disabled="disabled"
            @focus="focusSelectAllInput"
            @input="onQtyOrPriceInput(line)"
          />
          <div v-else class="stock-invoice-line__value">{{ formatMoney(line.unit_price_net) }}</div>
        </div>
        <div class="stock-invoice-line__field">
          <div class="stock-invoice-line__label">{{ $t("STOCK.INVOICES.LINE_TOTAL_NET") }}</div>
          <StockLineField
            v-if="!disabled"
            v-model.number="line.line_total_net"
            type="number"
            :hint="currencyCode"
            :disabled="disabled"
            @focus="focusSelectAllInput"
            @input="onLineTotalInput(line)"
          />
          <div v-else class="stock-invoice-line__value">{{ formatMoney(line.line_total_net) }}</div>
        </div>
        <div class="stock-invoice-line__field">
          <div class="stock-invoice-line__label">{{ $t("STOCK.INVOICES.VAT") }}</div>
          <StockLineField
            v-if="!disabled"
            v-model.number="line.vat_rate"
            type="number"
            hint="%"
            :disabled="disabled"
            @focus="focusSelectAllInput"
            @input="emitChange"
          />
          <div v-else class="stock-invoice-line__value">{{ line.vat_rate }}%</div>
        </div>
      </div>
    </article>

    <div v-if="!disabled" class="mt-3">
      <v-btn small outlined color="primary" :disabled="unitsLoading" @click="addBlankLine">
        <v-icon left small>mdi-plus</v-icon>
        {{ $t("STOCK.INVOICES.ADD_LINE") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import StockProductLineFinder from "@/views/partials/stock/StockProductLineFinder.vue";
import StockLineField from "@/views/partials/stock/StockLineField.vue";
import stockDocumentLinesMixin from "@/views/partials/stock/stockDocumentLines.mixin.js";
import stockDocumentTotalsMixin from "@/views/partials/stock/stockDocumentTotals.mixin.js";
import { GET_STOCK_UNIT_OPTIONS } from "@/store/stock/stockUnits.module";
import { invoiceProductDescription } from "@/common/stock/invoiceProductDescription";

export default {
  name: "StockInvoiceLinesEditor",
  components: { StockProductLineFinder, StockLineField },
  mixins: [stockDocumentLinesMixin, stockDocumentTotalsMixin],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    currencyCode: {
      type: String,
      default: "",
    },
    defaultVatRate: {
      type: [Number, String],
      default: null,
    },
    documentLocale: {
      type: String,
      default: "bg",
    },
  },
  data() {
    return {
      localLines: [],
      lineQuantityKey: "quantity",
      units: [],
      unitsLoading: false,
    };
  },
  created() {
    this.loadUnits();
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(lines) {
        this.localLines = (lines || []).map((line) => ({ ...line }));
      },
    },
  },
  methods: {
    lineKey(line, index) {
      return `line-${line.id || index}-${line.stock_product_id || "blank"}`;
    },
    lineProductMeta(line) {
      const product = line.stock_product;
      if (!product) {
        return "";
      }
      const parts = [
        product.code,
        product.stock_product_category ? product.stock_product_category.label : null,
        product.temperature ? product.temperature.label : null,
        product.tags_subtitle,
        this.lineUnitCode(line)
          ? `${this.$t("STOCK.PRODUCTS.UNIT")}: ${this.lineUnitCode(line)}`
          : null,
      ].filter(Boolean);
      return parts.join(" · ");
    },
    formatMoney(value) {
      return `${Number(value || 0).toFixed(2)} ${this.currencyCode || ""}`.trim();
    },
    formatQty(value) {
      return Number(value || 0).toFixed(4).replace(/\.?0+$/, "");
    },
    emitChange() {
      this.$emit("input", this.localLines.map((line) => ({ ...line })));
    },
    loadUnits() {
      this.unitsLoading = true;
      return this.$store
        .dispatch(GET_STOCK_UNIT_OPTIONS)
        .then((response) => {
          this.units = response.data || [];
        })
        .catch(() => {
          this.units = [];
        })
        .finally(() => {
          this.unitsLoading = false;
        });
    },
    isBulgarianDocument() {
      return String(this.documentLocale || "bg").toLowerCase() !== "en";
    },
    printedCode(unit) {
      if (!unit) {
        return "";
      }
      if (this.isBulgarianDocument()) {
        return unit.code_bg || unit.code || "";
      }
      return unit.code || "";
    },
    printedUnit(line) {
      const unit = line.unit || this.units.find((item) => item.id === line.unit_id);
      return this.printedCode(unit) || "—";
    },
    unitOptionLabel(unit) {
      return this.printedCode(unit) || unit.name_bg || unit.name || "";
    },
    unitOptionsFor(line) {
      const options = this.units.map((unit) => ({
        ...unit,
        label: this.unitOptionLabel(unit),
      }));
      if (line.unit_id && line.unit && !options.some((unit) => unit.id === line.unit_id)) {
        options.unshift({
          ...line.unit,
          label: this.unitOptionLabel(line.unit),
        });
      }
      return options;
    },
    onUnitSelected(line) {
      const unit = this.units.find((item) => item.id === line.unit_id) || line.unit || null;
      line.unit = unit;
      line.unit_code = unit ? unit.code : null;
      this.emitChange();
    },
    assignUnit(line, unit) {
      line.unit_id = unit ? unit.id : null;
      line.unit = unit || null;
      line.unit_code = unit ? unit.code : null;
    },
    assignDefaultManualUnit(line) {
      const pcs = this.units.find((unit) => String(unit.code || "").toLowerCase() === "pcs");
      if (pcs) {
        this.assignUnit(line, pcs);
      }
    },
    blankLine() {
      return {
        description: "",
        stock_product_id: null,
        stock_product: null,
        quantity: 1,
        unit_id: null,
        unit_price_net: 0,
        line_total_net: 0,
        vat_rate: this.defaultVatRate != null ? Number(this.defaultVatRate) : 0,
        hs_code: "",
      };
    },
    addBlankLine() {
      const line = this.blankLine();
      this.assignDefaultManualUnit(line);
      this.localLines.push(line);
      this.emitChange();
    },
    removeLine(index) {
      this.localLines.splice(index, 1);
      this.emitChange();
    },
    addProductFromFinder(product) {
      const line = this.blankLine();
      line.stock_product_id = product.id;
      line.stock_product = product;
      line.description = invoiceProductDescription(product);
      line.hs_code = product.code || "";
      line._product_label = line.description;
      this.resolveLineUnitFromProduct(line, product);
      line.unit = product.primary_unit || product.unit || line.unit || null;
      this.localLines.push(line);
      this.emitChange();
    },
    onQtyOrPriceInput(line) {
      line.unit_price_net = Number(line.unit_price_net || 0);
      line.quantity = Number(line.quantity || 0);
      line.line_total_net = this.computeLineNetFromUnit(
        { ...line, unit_cost_net: line.unit_price_net },
        this.lineQuantityKey
      );
      this.emitChange();
    },
    onLineTotalInput(line) {
      const qty = Number(line.quantity || 0);
      const total = Number(line.line_total_net || 0);
      if (qty) {
        line.unit_price_net = this.roundMoney(total / qty);
      }
      this.emitChange();
    },
  },
};
</script>

<style scoped>
.stock-invoice-lines {
  container-type: inline-size;
}
.stock-invoice-lines__empty {
  text-align: center;
  padding: 24px 8px;
}
.stock-invoice-line {
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  background: #fff;
  padding: 10px 12px 12px;
  margin-bottom: 10px;
}
.stock-invoice-line__name {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.stock-invoice-line__index {
  flex: 0 0 24px;
  padding-top: 8px;
  color: #757575;
  font-weight: 600;
  text-align: center;
}
.stock-invoice-line__description {
  flex: 1 1 auto;
  min-width: 0;
}
.stock-invoice-line__textarea {
  margin-top: 0;
  padding-top: 0;
}
.stock-invoice-line__textarea :deep(.v-input__slot) {
  background: #f7f7f7 !important;
}
.stock-invoice-line__textarea :deep(textarea) {
  line-height: 1.4;
  margin-top: 4px;
}
.stock-invoice-line__description-text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-weight: 500;
  line-height: 1.4;
  padding: 8px 2px 2px;
}
.stock-invoice-line__meta {
  margin-top: 4px;
  line-height: 1.35;
}
.stock-invoice-line__remove {
  flex: 0 0 auto;
  margin-top: 2px;
}
.stock-invoice-line__field {
  min-width: 0;
}
.stock-invoice-line__field :deep(.v-input) {
  max-width: 100%;
}
.stock-invoice-line__fields {
  display: grid;
  grid-template-columns:
    minmax(0, 1.15fr)
    minmax(0, 1.45fr)
    minmax(0, 0.85fr)
    minmax(0, 1.15fr)
    minmax(0, 1.15fr)
    minmax(0, 0.7fr);
  gap: 8px 10px;
  margin-top: 10px;
  padding-left: 32px;
}
.stock-invoice-line__label {
  font-size: 12px;
  line-height: 1.2;
  color: #757575;
  margin-bottom: 4px;
}
.stock-invoice-line__value {
  min-height: 36px;
  padding: 8px 2px;
  font-variant-numeric: tabular-nums;
}
.stock-invoice-unit {
  margin-top: 0;
  padding-top: 0;
}
.stock-invoice-unit :deep(.v-input__slot) {
  min-height: 36px !important;
}
.stock-invoice-line__field :deep(.stock-line-field__input) {
  width: 100%;
}
.stock-invoice-line__field :deep(.stock-line-field__input .v-input__slot) {
  min-height: 36px !important;
}
@container (max-width: 860px) {
  .stock-invoice-line__fields {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@container (max-width: 520px) {
  .stock-invoice-line__fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding-left: 0;
  }
}
</style>
